import React, {
  FC,
  PointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AlertTriangle,
  Check,
  ChevronLeft,
  Dumbbell,
  Pause,
  Play,
  Plus,
  Zap,
} from "lucide-react";
import { useIonRouter, UseIonRouterResult } from "@ionic/react";
import { Content, DefaultPage } from "@shared/layout/DefaultPage";
import { ExerciseInWorkout } from "@entities/exercise";
import {
  useGoToTrainingExercise,
  WorkoutType,
} from "@shared/hooks/trainingsNavigation/base";
import {
  WorkoutHistoryProxy,
  scheduleTrainingListsInvalidation,
} from "@entities/training";
import { workoutExecutionStore } from "@features/workout/execution/workoutExecutionStore";
import { useTimer } from "@features/TimeOut";
import { formatTime } from "@shared/helpers/timeFormatting";
import { getMediaRoleUrl, getMediaUrl } from "@shared/helpers/getMediaUrl";
import { ROUTES } from "@shared/constants/routes";
import { LocalStorageKeys } from "@shared/constants/LocalStorageKeys";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { FullExerciseInfoPage } from "@widgets/exercise/FullExerciseInfo";
import { ExerciseInfoVideo } from "@widgets/exercise/ExerciseDetailsView";
import { useUserService } from "@entities/User";
import TisitLogo from "@assets/icons/tisit_logo.png";
import {
  addWorkoutExecutionResult,
  buildWorkoutCompletionPath,
  getWorkoutWallClockDuration,
  saveWorkoutCompletionSnapshot,
} from "../lib/workoutCompletionSnapshot";
import "./workout-execution-flow.css";

type ExecutionMode = "cardio" | "strength";

type StrengthSet = {
  reps: number;
  weight: number;
};

type ExerciseCompletionOverride = {
  skipped?: boolean;
  cardioDuration?: number;
  cardioRestTime?: number;
  mode?: ExecutionMode;
};

interface WorkoutExecutionFlowProps {
  exercise: ExerciseInWorkout;
  workoutType: WorkoutType;
  trainingID: number;
  exerciseOrder: number;
  initialMode?: ExecutionMode;
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const buildNumberRange = (min: number, max: number) =>
  Array.from({ length: max - min + 1 }, (_, index) => min + index);

const getDefaultStrengthSet = (exercise: ExerciseInWorkout): StrengthSet => ({
  reps: exercise.reps || 9,
  weight: 0,
});

const isSameStrengthSet = (left: StrengthSet, right: StrengthSet) =>
  left.reps === right.reps && left.weight === right.weight;

const getNextExerciseLabel = (exerciseOrder: number) => {
  const nextExercise = workoutExecutionStore.getExerciseByOrder(
    exerciseOrder + 1,
  );
  return nextExercise?.exercise_detail.name || "Завершить тренировку";
};

const TIMER_ARC_DEGREES = 234;
const DEFAULT_CARDIO_WORK_SECONDS = 40;
const CARDIO_REST_SECONDS = 15;
const STRENGTH_MODE_STORAGE_PREFIX = "workoutExecution:lastStrengthMode";
const LEGACY_PROGRAM_FINISH_PATH = "/program/finish";
const PROGRAM_WEEK_PATH_REGEX = /^\/program\/([^/]+)\/week\/([^/]+)$/;

const getStrengthModeStorageKey = (trainingID: number) =>
  `${STRENGTH_MODE_STORAGE_PREFIX}:${trainingID}`;

const getLastStrengthMode = (trainingID: number): ExecutionMode | null => {
  try {
    const value = window.sessionStorage.getItem(
      getStrengthModeStorageKey(trainingID),
    );
    return value === "cardio" || value === "strength" ? value : null;
  } catch {
    return null;
  }
};

const setLastStrengthMode = (trainingID: number, mode: ExecutionMode) => {
  try {
    window.sessionStorage.setItem(getStrengthModeStorageKey(trainingID), mode);
  } catch {
    return;
  }
};

const getWorkoutReturnPath = () => {
  try {
    const storedPath = window.localStorage.getItem(
      LocalStorageKeys.workoutRedirectURL,
    );

    if (!storedPath) {
      return ROUTES.overall.main;
    }

    const returnUrl = new URL(storedPath, window.location.origin);

    if (returnUrl.pathname === LEGACY_PROGRAM_FINISH_PATH) {
      const enrollmentId = returnUrl.searchParams.get("enrollmentId");
      const weekNumber = returnUrl.searchParams.get("weekNumber");

      if (enrollmentId && weekNumber) {
        return ROUTES.program.week
          .replace(":enrollmentId", enrollmentId)
          .replace(":weekNumber", weekNumber);
      }

      return ROUTES.overall.main;
    }

    return `${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`;
  } catch {
    return ROUTES.overall.main;
  }
};

const getProgramWorkoutReturnContext = ():
  | { enrollmentId: string; weekNumber: string }
  | null => {
  try {
    const returnUrl = new URL(getWorkoutReturnPath(), window.location.origin);
    const match = returnUrl.pathname.match(PROGRAM_WEEK_PATH_REGEX);

    if (!match) {
      return null;
    }

    return {
      enrollmentId: decodeURIComponent(match[1]),
      weekNumber: decodeURIComponent(match[2]),
    };
  } catch {
    return null;
  }
};

const invalidateProgramWorkoutReturn = (queryClient: QueryClient) => {
  const programContext = getProgramWorkoutReturnContext();

  if (!programContext) {
    return;
  }

  void queryClient.invalidateQueries({
    queryKey: ["enrollment", programContext.enrollmentId],
  });
  void queryClient.invalidateQueries({
    queryKey: [
      "programWeek",
      programContext.enrollmentId,
      `weekNumber/${programContext.weekNumber}`,
    ],
  });
  void queryClient.invalidateQueries({
    queryKey: ["main_page", "programs"],
  });
};

const getInitialExecutionMode = (
  trainingID: number,
  isStrengthExercise: boolean,
  initialMode?: ExecutionMode,
): ExecutionMode => {
  if (initialMode) return initialMode;
  if (!isStrengthExercise) return "cardio";
  return getLastStrengthMode(trainingID) || "strength";
};

const ExecutionHeader: FC<{
  title: string;
  onBack: () => void;
}> = ({ title, onBack }) => (
  <header className="workout-execution-header">
    <button
      type="button"
      className="workout-execution-back"
      onClick={onBack}
      aria-label="Назад"
    >
      <ChevronLeft size={28} strokeWidth={2} />
    </button>
    <h1>{title}</h1>
    <img src={TisitLogo} alt="TISIT" />
  </header>
);

const ModeToggle: FC<{
  mode: ExecutionMode;
  isStrengthAvailable: boolean;
  onChange: (mode: ExecutionMode) => void;
}> = ({ mode, isStrengthAvailable, onChange }) => (
  <div
    className="workout-execution-mode-toggle"
    role="tablist"
    aria-label="Режим упражнения"
  >
    <button
      type="button"
      className={mode === "cardio" ? "is-active" : "not-active"}
      onClick={() => onChange("cardio")}
    >
      <Zap size={13} fill="currentColor" />
      Кардио
    </button>
    {isStrengthAvailable && (
      <button
        type="button"
        className={mode === "strength" ? "is-active" : "not-active"}
        onClick={() => onChange("strength")}
      >
        <Dumbbell size={13} />
        Силовой
      </button>
    )}
  </div>
);

const WorkoutHero: FC<{
  exercise: ExerciseInWorkout;
  exerciseOrder: number;
  onInfoClick?: () => void;
}> = ({ exercise, exerciseOrder, onInfoClick }) => {
  const exerciseMedia = exercise.exercise_detail.media;
  const videoUrl = getMediaUrl(exerciseMedia.video);
  const posterUrl = getMediaRoleUrl(exerciseMedia, [
    "cover",
    "icon",
    "muscle_groups",
  ]);

  return (
    <div className="workout-execution-hero">
      <ExerciseInfoVideo
        videoUrl={videoUrl}
        posterUrl={posterUrl}
        title={exercise.exercise_detail.name}
      />
      <div className="workout-execution-media-count">
        {exerciseOrder}/{workoutExecutionStore.exercisesAmount}
      </div>
      <button
        type="button"
        className="workout-execution-info-button"
        onClick={onInfoClick}
        aria-label="Информация об упражнении"
      >
        <span>i</span>
      </button>
    </div>
  );
};

const ModeBlockedModal: FC<{
  onClose: () => void;
}> = ({ onClose }) => (
  <div className="workout-execution-modal-overlay" onClick={onClose}>
    <div
      className="workout-execution-mode-modal"
      onClick={(event) => event.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="workout-execution-mode-modal-title"
    >
      <div className="workout-execution-mode-modal-icon">
        <AlertTriangle size={25} strokeWidth={2.2} />
      </div>
      <h2 id="workout-execution-mode-modal-title">Режим уже начат</h2>
      <p>
        Переключиться можно до завершения первого подхода или первого раунда.
        Дальше нужно закончить выбранный режим.
      </p>
      <button type="button" onClick={onClose}>
        Понятно
      </button>
    </div>
  </div>
);

const CardioRoundTimer: FC<{
  currentRound: number;
  isCompleted: boolean;
  phase: "work" | "rest";
  onRoundComplete: (duration: number) => void;
  onSkip: (duration: number) => void;
  roundsAmount: number;
  seconds: number;
}> = ({
  currentRound,
  isCompleted,
  phase,
  onRoundComplete,
  onSkip,
  roundsAmount,
  seconds,
}) => {
  const completedRef = useRef(false);
  const remainingTimeRef = useRef(seconds);
  const handleTimerEnd = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    onRoundComplete(Math.round(seconds - remainingTimeRef.current));
  }, [onRoundComplete, seconds]);
  const workoutTime = useTimer({
    seconds,
    onEnd: handleTimerEnd,
  });

  useEffect(() => {
    remainingTimeRef.current = workoutTime.remainingTime;
  }, [workoutTime.remainingTime]);

  const remainingProgress = clamp(workoutTime.remainingTime / seconds, 0, 1);

  return (
    <section
      className={`workout-execution-cardio ${isCompleted ? "is-completed" : ""}`}
    >
      <div
        className="workout-execution-timer-ring"
        style={
          {
            "--timer-progress": `${remainingProgress * TIMER_ARC_DEGREES}deg`,
          } as React.CSSProperties
        }
      >
        <div className="workout-execution-timer-inner">
          <span>
            Раунд {currentRound}/{roundsAmount}
          </span>
          <strong>{formatTime(workoutTime.remainingTime)}</strong>
          <small>{phase === "rest" ? "Отдых" : "Подход"}</small>
        </div>
      </div>
      {!isCompleted && (
        <div className="workout-execution-time-actions">
          <button
            type="button"
            onClick={() => {
              workoutTime.addTime(-10);
            }}
          >
            -10 сек
          </button>
          <span className="workout-execution-time-label">
            <span>Настроить</span>
            <span>время</span>
          </span>
          <button
            type="button"
            onClick={() => {
              workoutTime.addTime(10);
            }}
          >
            +10 сек
          </button>
        </div>
      )}
      {!isCompleted && (
        <div className="workout-execution-footer-actions workout-execution-cardio-actions">
          <button
            type="button"
            className="workout-execution-secondary"
            onClick={() => {
              completedRef.current = true;
              workoutTime.toggleTimerPlaying(false);
              onSkip(Math.round(seconds - remainingTimeRef.current));
            }}
          >
            {phase === "rest" ? "Пропустить отдых" : "Пропустить"}
          </button>
          <button
            type="button"
            className="workout-execution-primary"
            onClick={() => {
              workoutTime.toggleTimerPlaying();
            }}
          >
            {workoutTime.isTimerPlaying ? (
              <Pause
                className="workout-execution-primary-icon"
                size={24}
                strokeWidth={3}
                fill="currentColor"
              />
            ) : (
              <Play
                className="workout-execution-primary-icon"
                size={24}
                strokeWidth={3}
                fill="currentColor"
              />
            )}
            {workoutTime.isTimerPlaying ? "Пауза" : "Начать"}
          </button>
        </div>
      )}
    </section>
  );
};

const CardioTimer: FC<{
  exercise: ExerciseInWorkout;
  isCompleted: boolean;
  onComplete: (duration: number, restTime: number) => void;
  onModeLock: () => void;
  onSkip: () => void;
}> = ({ exercise, isCompleted, onComplete, onModeLock, onSkip }) => {
  const roundsAmount = Math.max(exercise.sets || 1, 1);
  const [currentRound, setCurrentRound] = useState(1);
  const [phase, setPhase] = useState<"work" | "rest">("work");
  const completedRef = useRef(false);
  const totalWorkDurationRef = useRef(0);
  const totalRestTimeRef = useRef(0);
  const workSeconds = exercise.duration || DEFAULT_CARDIO_WORK_SECONDS;
  const phaseSeconds = phase === "rest" ? CARDIO_REST_SECONDS : workSeconds;

  useEffect(() => {
    setCurrentRound(1);
    setPhase("work");
    completedRef.current = false;
    totalWorkDurationRef.current = 0;
    totalRestTimeRef.current = 0;
  }, [exercise.order, roundsAmount]);

  const handleRoundComplete = useCallback(
    (duration: number) => {
      if (completedRef.current) return;

      if (phase === "rest") {
        totalRestTimeRef.current += duration;
        setCurrentRound((round) => Math.min(round + 1, roundsAmount));
        setPhase("work");
        return;
      }

      const nextTotalDuration = totalWorkDurationRef.current + duration;
      totalWorkDurationRef.current = nextTotalDuration;
      onModeLock();
      if (currentRound < roundsAmount) {
        setPhase("rest");
        return;
      }

      completedRef.current = true;
      onComplete(Math.round(nextTotalDuration), Math.round(totalRestTimeRef.current));
    },
    [currentRound, onComplete, onModeLock, phase, roundsAmount],
  );

  const handleSkip = useCallback(
    (duration: number) => {
      if (phase === "rest") {
        totalRestTimeRef.current += duration;
        setCurrentRound((round) => Math.min(round + 1, roundsAmount));
        setPhase("work");
        return;
      }

      completedRef.current = true;
      onSkip();
    },
    [onSkip, phase, roundsAmount],
  );

  return (
    <CardioRoundTimer
      key={`${exercise.order}-${currentRound}-${phase}`}
      currentRound={currentRound}
      isCompleted={isCompleted}
      phase={phase}
      onRoundComplete={handleRoundComplete}
      onSkip={handleSkip}
      roundsAmount={roundsAmount}
      seconds={phaseSeconds}
    />
  );
};

const StrengthWheelColumn: FC<{
  ariaLabel: string;
  max: number;
  min: number;
  value: number;
  onChange: (value: number) => void;
}> = ({ ariaLabel, max, min, value, onChange }) => {
  const options = useMemo(() => buildNumberRange(min, max), [max, min]);
  const columnRef = useRef<HTMLDivElement | null>(null);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getRowHeight = useCallback(() => {
    const element = columnRef.current;
    if (!element) return 42;

    const rowHeight = Number.parseFloat(
      window.getComputedStyle(element).getPropertyValue("--picker-row-height"),
    );

    return Number.isFinite(rowHeight) && rowHeight > 0 ? rowHeight : 42;
  }, []);

  const scrollToValue = useCallback(
    (nextValue: number, behavior: ScrollBehavior = "smooth") => {
      const element = columnRef.current;
      if (!element) return;

      element.scrollTo({
        top: (nextValue - min) * getRowHeight(),
        behavior,
      });
    },
    [getRowHeight, min],
  );

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      scrollToValue(value, "auto");
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [scrollToValue, value]);

  useEffect(
    () => () => {
      if (settleTimerRef.current) {
        clearTimeout(settleTimerRef.current);
      }
    },
    [],
  );

  const settleScroll = () => {
    const element = columnRef.current;
    if (!element) return;

    const nextIndex = clamp(
      Math.round(element.scrollTop / getRowHeight()),
      0,
      options.length - 1,
    );
    const nextValue = options[nextIndex];

    if (nextValue !== value) {
      onChange(nextValue);
    }
    scrollToValue(nextValue);
  };

  const handleScroll = () => {
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
    }
    settleTimerRef.current = setTimeout(settleScroll, 90);
  };

  return (
    <div
      ref={columnRef}
      className="workout-execution-picker-column"
      role="listbox"
      aria-label={ariaLabel}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      onScroll={handleScroll}
    >
      {options.map((option) => (
        <button
          type="button"
          key={option}
          className={option === value ? "is-active" : ""}
          role="option"
          aria-selected={option === value}
          onClick={() => {
            onChange(option);
            scrollToValue(option);
          }}
        >
          {option}
        </button>
      ))}
    </div>
  );
};

const StrengthPicker: FC<{
  currentSet: number;
  direction: "backward" | "forward";
  setsAmount: number;
  value: StrengthSet;
  onChange: (value: StrengthSet) => void;
  onOpenSheet: () => void;
}> = ({ currentSet, direction, setsAmount, value, onChange, onOpenSheet }) => {
  return (
    <section
      key={currentSet}
      className={`workout-execution-strength is-${direction}`}
    >
      <div className="workout-execution-picker">
        <div className="workout-execution-picker-head">
          <span>Повторений</span>
          <span>Вес</span>
        </div>
        <div className="workout-execution-picker-body">
          <div className="workout-execution-picker-highlight" />
          <StrengthWheelColumn
            ariaLabel="Повторений"
            min={1}
            max={80}
            value={value.reps}
            onChange={(reps) => onChange({ ...value, reps })}
          />
          <StrengthWheelColumn
            ariaLabel="Вес"
            min={0}
            max={250}
            value={value.weight}
            onChange={(weight) => onChange({ ...value, weight })}
          />
        </div>
      </div>
      <button
        type="button"
        className="workout-execution-set-pill"
        onClick={onOpenSheet}
      >
        <span className="workout-execution-set-burger" aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
        Подход {currentSet} из {setsAmount}
      </button>
    </section>
  );
};

const SetInlineField: FC<{
  label: string;
  max: number;
  min: number;
  onChange: (value: number) => void;
  value: number;
}> = ({ label, max, min, onChange, value }) => {
  const setValue = (nextValue: number) => {
    onChange(clamp(nextValue, min, max));
  };

  return (
    <label className="workout-execution-set-field">
      <input
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={value}
        onChange={(event) => {
          const nextValue = Number(event.target.value);
          if (Number.isFinite(nextValue)) {
            setValue(nextValue);
          }
        }}
        aria-label={label}
      />
      <span>{label}</span>
    </label>
  );
};

const SetInlineRow: FC<{
  onChange: (value: StrengthSet) => void;
  set: StrengthSet;
}> = ({ onChange, set }) => (
  <div className="workout-execution-set-row">
    <SetInlineField
      label="повторений"
      value={set.reps || 0}
      min={1}
      max={100}
      onChange={(reps) => onChange({ ...set, reps })}
    />
    <span className="workout-execution-set-row-separator">×</span>
    <SetInlineField
      label="вес"
      value={set.weight || 0}
      min={0}
      max={250}
      onChange={(weight) => onChange({ ...set, weight })}
    />
  </div>
);

const SetsSheet: FC<{
  sets: StrengthSet[];
  onSetChange: (setIndex: number, value: StrengthSet) => void;
  onClose: () => void;
}> = ({ sets, onSetChange, onClose }) => (
  <>
    <button
      type="button"
      className="workout-execution-sets-backdrop"
      aria-label="Закрыть подходы"
      onClick={onClose}
    />
    <section
      className="workout-execution-sets-sheet"
      role="dialog"
      aria-modal="true"
      aria-labelledby="workout-execution-sets-title"
    >
      <div className="workout-execution-sets-handle" aria-hidden="true" />
      <div className="workout-execution-sets-content">
        <section className="workout-execution-sets-section">
          <h2 id="workout-execution-sets-title">Все подходы</h2>
          <div className="workout-execution-sets-list">
            {sets.map((set, index) => (
              <SetInlineRow
                key={index}
                set={set}
                onChange={(value) => onSetChange(index, value)}
              />
            ))}
          </div>
        </section>
      </div>
    </section>
  </>
);

const CompletionSwipe: FC<{
  title: string;
  isPending?: boolean;
  onComplete: () => void;
}> = ({ title, isPending, onComplete }) => {
  const [dragX, setDragX] = useState(0);
  const dragXRef = useRef(0);
  const startX = useRef<number | null>(null);
  const trackWidth = useRef(0);
  const maxDrag = Math.max(0, trackWidth.current - 64);
  const swipeProgress = maxDrag > 0 ? clamp(dragX / maxDrag, 0, 1) : 0;
  const labelOpacity = clamp(1 - swipeProgress * 1.25, 0, 1);
  const staticCheckOpacity = clamp(1 - swipeProgress * 8, 0, 1);
  const thumbCheckOpacity = clamp((swipeProgress - 0.68) / 0.22, 0, 1);

  const handlePointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    if (isPending) return;
    startX.current = event.clientX;
    trackWidth.current = event.currentTarget.offsetWidth;
    dragXRef.current = 0;
    setDragX(0);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    if (startX.current === null || isPending) return;
    const nextDragX = clamp(
      event.clientX - startX.current,
      0,
      Math.max(0, trackWidth.current - 64),
    );
    dragXRef.current = nextDragX;
    setDragX(nextDragX);
  };

  const finishSwipe = () => {
    if (
      !isPending &&
      trackWidth.current > 0 &&
      dragXRef.current > trackWidth.current * 0.52
    ) {
      onComplete();
    }
    dragXRef.current = 0;
    setDragX(0);
    startX.current = null;
  };

  return (
    <div className="workout-execution-completion">
      <button
        type="button"
        className="workout-execution-swipe"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishSwipe}
        onPointerCancel={finishSwipe}
        style={
          {
            "--swipe-x": `${dragX}px`,
            "--swipe-progress": `${swipeProgress * 100}%`,
            "--swipe-label-opacity": labelOpacity,
            "--swipe-static-check-opacity": staticCheckOpacity,
            "--swipe-thumb-check-opacity": thumbCheckOpacity,
          } as React.CSSProperties
        }
      >
        <span
          className="workout-execution-swipe-thumb"
          style={{ transform: `translate3d(${dragX}px, 0, 0)` }}
        >
          <ChevronLeft
            className="workout-execution-swipe-arrow-icon"
            size={24}
          />
          <Check
            className="workout-execution-swipe-thumb-check-icon"
            size={22}
          />
        </span>
        <strong>{isPending ? "Сохраняем..." : title}</strong>
        <span className="workout-execution-swipe-check">
          <Check size={20} />
        </span>
      </button>
    </div>
  );
};

export const WorkoutExecutionFlow: FC<WorkoutExecutionFlowProps> = ({
  exercise,
  workoutType,
  trainingID,
  exerciseOrder,
  initialMode,
}) => {
  const isStrengthExercise = exercise.exercise_detail.type === "strength";
  const [mode, setMode] = useState<ExecutionMode>(() =>
    getInitialExecutionMode(trainingID, isStrengthExercise, initialMode),
  );
  const [isDescription, setIsDescription] = useState(false);
  const [isCardioCompleted, setIsCardioCompleted] = useState(false);
  const [isCardioModeLocked, setIsCardioModeLocked] = useState(false);
  const [cardioDuration, setCardioDuration] = useState(0);
  const [cardioRestTime, setCardioRestTime] = useState(0);
  const [isModeBlockedModalOpen, setIsModeBlockedModalOpen] = useState(false);
  const [currentSet, setCurrentSet] = useState(1);
  const [setTransitionDirection, setSetTransitionDirection] =
    useState<"backward" | "forward">("forward");
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const targetSetsAmount = Math.max(exercise.sets || 1, 1);
  const [sets, setSets] = useState<StrengthSet[]>(
    Array.from({ length: targetSetsAmount }, () =>
      getDefaultStrengthSet(exercise),
    ),
  );
  const setsAmount = sets.length;
  const nav: UseIonRouterResult = useIonRouter();
  const goToTrainingExercise = useGoToTrainingExercise();
  const queryClient = useQueryClient();
  const userService = useUserService();
  const exerciseStartedAtRef = useRef(Date.now());
  const [isCompleting, setIsCompleting] = useState(false);
  const exercisesAmount =
    workoutExecutionStore.exercisesAmount || exerciseOrder || 1;

  useEffect(() => {
    setMode(
      getInitialExecutionMode(trainingID, isStrengthExercise, initialMode),
    );
    setIsCardioCompleted(false);
    setIsCardioModeLocked(false);
    setCardioDuration(0);
    setCardioRestTime(0);
    setIsModeBlockedModalOpen(false);
    setCurrentSet(1);
    setSetTransitionDirection("forward");
    setIsCompleting(false);
    exerciseStartedAtRef.current = Date.now();
    setSets(
      Array.from({ length: Math.max(exercise.sets || 1, 1) }, () =>
        getDefaultStrengthSet(exercise),
      ),
    );
  }, [
    exercise.order,
    exercise.reps,
    exercise.sets,
    initialMode,
    isStrengthExercise,
    trainingID,
  ]);

  const goToNextExercise = () => {
    if (workoutType === "Training") {
      goToTrainingExercise(trainingID, exerciseOrder + 1, workoutType);
    }
  };

  const handlePreviousExercise = () => {
    nav.push(getWorkoutReturnPath(), "back");
  };

  const submitExerciseResult = (override?: ExerciseCompletionOverride) => {
    const executionMode = override?.mode || mode;
    if (isStrengthExercise) {
      setLastStrengthMode(trainingID, executionMode);
    }

    if (executionMode === "strength") {
      return WorkoutHistoryProxy.updateTrainingInfo(trainingID, exerciseOrder, {
        sets: sets
          .filter((set) => set.reps > 0)
          .map((set) => ({ reps: set.reps, weight: set.weight })),
        status: sets.some((set) => set.reps > 0) ? "completed" : "skipped",
      });
    }

    if (override?.skipped) {
      return WorkoutHistoryProxy.updateTrainingInfo(trainingID, exerciseOrder, {
        status: "skipped",
        duration: 0,
        rest_time: 0,
      });
    }

    return WorkoutHistoryProxy.updateTrainingInfo(trainingID, exerciseOrder, {
      status:
        (override?.cardioDuration ?? cardioDuration) > 0
          ? "completed"
          : "skipped",
      duration: override?.cardioDuration ?? cardioDuration,
      rest_time: override?.cardioRestTime ?? cardioRestTime,
    });
  };

  const getExerciseProgressResult = (
    override: ExerciseCompletionOverride | undefined,
    completedAt: number,
  ) => {
    const executionMode = override?.mode || mode;

    if (executionMode === "strength") {
      const isCompleted = sets.some((set) => set.reps > 0);
      return {
        durationSeconds: isCompleted
          ? getWorkoutWallClockDuration({
              completedAt,
              startedAt: exerciseStartedAtRef.current,
            })
          : 0,
        isCompleted,
      };
    }

    if (override?.skipped) {
      return {
        durationSeconds: 0,
        isCompleted: false,
      };
    }

    const workDuration = override?.cardioDuration ?? cardioDuration;
    const restDuration = override?.cardioRestTime ?? cardioRestTime;

    return {
      durationSeconds: Math.max(0, workDuration + restDuration),
      isCompleted: workDuration > 0,
    };
  };

  const completeExercise = (override?: ExerciseCompletionOverride) => {
    if (isCompleting) {
      return;
    }

    setIsCompleting(true);
    const isLastExercise = exercisesAmount === exerciseOrder;
    const exerciseUpdatePromise = submitExerciseResult(override);
    const completedAt = Date.now();
    const progressResult = getExerciseProgressResult(override, completedAt);

    if (isLastExercise) {
      void (async () => {
        try {
          await exerciseUpdatePromise;
          await WorkoutHistoryProxy.finishTraining(trainingID);
          saveWorkoutCompletionSnapshot({
            exerciseDurationSeconds: progressResult.durationSeconds,
            exercisesAmount,
            isExerciseCompleted: progressResult.isCompleted,
            trainingID,
            userWeightKg: userService?.data.weight,
          });
          nav.push(buildWorkoutCompletionPath(trainingID));
        } finally {
          scheduleTrainingListsInvalidation(queryClient);
          invalidateProgramWorkoutReturn(queryClient);
          setIsCompleting(false);
        }
      })();
      return;
    }

    addWorkoutExecutionResult({
      durationSeconds: progressResult.durationSeconds,
      isCompleted: progressResult.isCompleted,
      trainingID,
    });
    goToNextExercise();
    void exerciseUpdatePromise.finally(() => {
      setIsCompleting(false);
      scheduleTrainingListsInvalidation(queryClient);
    });
  };

  const currentSetValue = sets[currentSet - 1] || { reps: 0, weight: 0 };
  const isModeSwitchLocked =
    (mode === "cardio" && isCardioModeLocked) ||
    (mode === "strength" && currentSet > 1);
  const isPreviousSetButtonDisabled = currentSet === 1;
  const isAddSetButton =
    currentSet === setsAmount && !isPreviousSetButtonDisabled;
  const nextActionLabel =
    exercisesAmount === exerciseOrder
      ? "Завершить тренировку"
      : getNextExerciseLabel(exerciseOrder);

  if (isDescription) {
    return (
      <FullExerciseInfoPage
        exercise={exercise}
        setIsShowingDescription={setIsDescription}
      />
    );
  }

  return (
    <DefaultPage className="workout-execution-page" noPadding>
      <Content className="workout-execution-content">
        <div className="workout-execution-shell">
          <ExecutionHeader
            title={exercise.exercise_detail.name}
            onBack={handlePreviousExercise}
          />
          <WorkoutHero
            exercise={exercise}
            exerciseOrder={exerciseOrder}
            onInfoClick={() => setIsDescription(true)}
          />
          <ModeToggle
            mode={mode}
            isStrengthAvailable={isStrengthExercise}
            onChange={(nextMode) => {
              if (nextMode !== mode && isModeSwitchLocked) {
                setIsModeBlockedModalOpen(true);
                return;
              }
              setMode(nextMode);
              setIsCardioCompleted(false);
              setIsCardioModeLocked(false);
              setCardioDuration(0);
              setCardioRestTime(0);
            }}
          />
          {mode === "cardio" && (
            <CardioTimer
              key={`${exercise.order}-${mode}`}
              exercise={exercise}
              isCompleted={isCardioCompleted}
              onComplete={(duration, restTime) => {
                setCardioDuration(duration);
                setCardioRestTime(restTime);
                setIsCardioCompleted(true);
              }}
              onModeLock={() => setIsCardioModeLocked(true)}
              onSkip={() =>
                completeExercise({
                  skipped: true,
                  cardioDuration: 0,
                  cardioRestTime: 0,
                  mode: "cardio",
                })
              }
            />
          )}
          {mode === "strength" && (
            <StrengthPicker
              currentSet={currentSet}
              direction={setTransitionDirection}
              setsAmount={setsAmount}
              value={currentSetValue}
              onChange={(value) => {
                setSets((current) =>
                  current.map((set, index) =>
                    index === currentSet - 1 ? value : set,
                  ),
                );
              }}
              onOpenSheet={() => setIsSheetOpen(true)}
            />
          )}
          {mode === "cardio" && isCardioCompleted && (
            <CompletionSwipe
              title={nextActionLabel}
              isPending={isCompleting}
              onComplete={() => completeExercise()}
            />
          )}
          {mode === "strength" && (
            <div className="workout-execution-footer-actions workout-execution-strength-actions">
              <button
                type="button"
                className="workout-execution-secondary is-back"
                disabled={isPreviousSetButtonDisabled}
                aria-disabled={isPreviousSetButtonDisabled}
                onClick={() => {
                  if (isAddSetButton) {
                    const previousSet =
                      sets[sets.length - 1] || getDefaultStrengthSet(exercise);
                    setSetTransitionDirection("forward");
                    setSets((current) => [...current, previousSet]);
                    setCurrentSet(setsAmount + 1);
                    return;
                  }
                  setSetTransitionDirection("backward");
                  setCurrentSet((set) => Math.max(1, set - 1));
                }}
                aria-label={
                  isAddSetButton
                    ? "Добавить подход"
                    : "Предыдущий подход"
                }
              >
                {isAddSetButton ? (
                  <Plus size={24} />
                ) : (
                  <ChevronLeft size={24} />
                )}
              </button>
              <button
                type="button"
                className="workout-execution-primary"
                disabled={isCompleting}
                onClick={() => {
                  if (currentSet < setsAmount) {
                    setSets((current) => {
                      const currentSetIndex = currentSet - 1;
                      const nextSetIndex = currentSet;
                      const previousSet = current[currentSetIndex];
                      const nextSet = current[nextSetIndex];

                      if (!previousSet || !nextSet) {
                        return current;
                      }

                      if (!isSameStrengthSet(nextSet, getDefaultStrengthSet(exercise))) {
                        return current;
                      }

                      return current.map((set, index) =>
                        index === nextSetIndex ? previousSet : set,
                      );
                    });
                    setSetTransitionDirection("forward");
                    setCurrentSet((set) => set + 1);
                    return;
                  }
                  completeExercise();
                }}
              >
                {currentSet < setsAmount ? "Следующий подход" : nextActionLabel}
              </button>
            </div>
          )}
        </div>
      </Content>
      {isSheetOpen && (
        <SetsSheet
          sets={sets}
          onSetChange={(setIndex, value) => {
            setSets((current) =>
              current.map((set, index) => (index === setIndex ? value : set)),
            );
          }}
          onClose={() => setIsSheetOpen(false)}
        />
      )}
      {isModeBlockedModalOpen && (
        <ModeBlockedModal onClose={() => setIsModeBlockedModalOpen(false)} />
      )}
    </DefaultPage>
  );
};
